import { useState } from 'react'
import type { PmmRepository } from '../data/repository'
import type { Capacity } from '../domain/dailyEngine'
import { TodayWorkspace } from './DailyWorkspace'
import { PracticeLibrary } from './PracticeLibrary'
import { FoundationWorkspace } from './FoundationWorkspace'
import { WeeklyCouncil } from './WeeklyCouncil'
import { PostOnboardingGuide } from './PostOnboardingGuide'
import { JourneyModeControl } from './JourneyModeControl'
import { PrimalMissionCard } from './PrimalMissionCard'
import { PartnerConnectionCard } from './PartnerConnectionCard'
import '../weekly.css'
import '../journey-architecture.css'
import '../journey-guide-collapsible.css'
import '../journey-experience.css'

interface UserJourneyWorkspaceProps { repository: PmmRepository }
type PrimaryJourney = 'foundation' | 'personal'
type PersonalSurface = 'today' | 'library' | 'weekly'

export function UserJourneyWorkspace({ repository }: UserJourneyWorkspaceProps) {
  const [journey, setJourney] = useState<PrimaryJourney>('personal')
  const [personalSurface, setPersonalSurface] = useState<PersonalSurface>('today')
  const [capacity, setCapacity] = useState<Capacity>('normal')

  return <div className="user-journey-shell">
    <section className="journey-command" aria-labelledby="journey-command-title">
      <div className="journey-command-copy">
        <p className="eyebrow">Two journeys · one whole man</p>
        <h1 id="journey-command-title"><span>Build what every man needs.</span><em>Work with what is yours.</em></h1>
        <p>The Foundation builds the shared backbone of healthy masculinity. Your Primal Path brings it into your real life—so you can become a grounded, self-respecting masculine man who leads himself, loves well, repairs cleanly and is present for the people who matter.</p>
      </div>
    </section>

    <PrimalMissionCard />
    <PartnerConnectionCard />

    <nav className="journey-primary-tabs" role="tablist" aria-label="Choose your journey">
      <button
        id="foundation-journey-tab"
        type="button"
        role="tab"
        aria-selected={journey === 'foundation'}
        aria-controls="foundation-journey-panel"
        className={journey === 'foundation' ? 'is-active' : ''}
        onClick={() => setJourney('foundation')}
      >
        <span className="journey-tab-number" aria-hidden="true">01</span>
        <span className="journey-tab-copy"><small>Shared · self-paced · 16 weeks</small><strong>The Primal Foundation</strong><span>The essential inner work, healthy masculinity, relationship skills and grounded practices every man can build.</span></span>
        <span className="journey-tab-action">Enter Foundation <i aria-hidden="true">→</i></span>
      </button>
      <button
        id="personal-journey-tab"
        type="button"
        role="tab"
        aria-selected={journey === 'personal'}
        aria-controls="personal-journey-panel"
        className={journey === 'personal' ? 'is-active' : ''}
        onClick={() => setJourney('personal')}
      >
        <span className="journey-tab-number" aria-hidden="true">02</span>
        <span className="journey-tab-copy"><small>Personal · Coach-reviewed · evolving</small><strong>Your Primal Path</strong><span>Guidance shaped around your life, relationships, capacity and the truth you choose to share.</span></span>
        <span className="journey-tab-action">Enter your path <i aria-hidden="true">→</i></span>
      </button>
    </nav>

    {journey === 'foundation' ? <section className="journey-panel" id="foundation-journey-panel" role="tabpanel" aria-labelledby="foundation-journey-tab">
      <PostOnboardingGuide surface="foundation" collapsible />
      <FoundationWorkspace repository={repository} capacity={capacity} />
    </section> : <section className="journey-panel" id="personal-journey-panel" role="tabpanel" aria-labelledby="personal-journey-tab">
      <div className="personal-path-heading">
        <div><p className="eyebrow">Your Primal Path</p><h2>Personal guidance without the noise.</h2></div>
        <p>Start with what is useful today, return to your complete library when needed, and use Weekly Council to help Coach understand what should come next.</p>
      </div>
      <nav className="user-journey-nav" role="tablist" aria-label="Your Primal Path sections">
        <button type="button" role="tab" aria-selected={personalSurface === 'today'} className={personalSurface === 'today' ? 'is-active' : ''} onClick={() => setPersonalSurface('today')}><strong>Today</strong><span>One useful next step</span></button>
        <button type="button" role="tab" aria-selected={personalSurface === 'library'} className={personalSurface === 'library' ? 'is-active' : ''} onClick={() => setPersonalSurface('library')}><strong>Your library</strong><span>Search, pin and archive</span></button>
        <button type="button" role="tab" aria-selected={personalSurface === 'weekly'} className={personalSurface === 'weekly' ? 'is-active' : ''} onClick={() => setPersonalSurface('weekly')}><strong>Weekly Council</strong><span>Review and prepare Coach</span></button>
      </nav>
      <PostOnboardingGuide surface={personalSurface} collapsible />
      {personalSurface === 'today'
        ? <TodayWorkspace repository={repository} capacity={capacity} />
        : personalSurface === 'library'
          ? <PracticeLibrary repository={repository} />
          : <WeeklyCouncil repository={repository} />}
    </section>}
    <JourneyModeControl value={capacity} onChange={setCapacity} />
  </div>
}
